import React from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { RichTextEditor } from './Editor/plugins/RichTextEditor';

interface InputCommentProps {
  onChange?: (value: string) => void;
  value: string;
  readonly?: boolean;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  shadow?: boolean;
  /**
   * When true (the default for readonly bubbles), neutralise the
   * `RichTextEditor` internal background/border so the comment reads
   * as a single message bubble (WhatsApp-style) instead of showing
   * the editor's own paper-coloured field on top of the parent
   * bubble. Pass `false` for inputs that want the full editor chrome.
   */
  transparentSurface?: boolean;
}

const InputComment: React.FC<InputCommentProps> = ({
  onChange,
  value,
  readonly,
  autoFocus,
  onKeyDown,
  onBlur,
  shadow = false,
  transparentSurface = true,
}) => {
  if (readonly) {
    // Strip the editor's own background/border so the bubble colour
    // of the parent (CommentsSection) shows through and the comment
    // renders as a single coloured message — no embedded white field.
    const transparentSx = transparentSurface
      ? {
          border: 'none !important',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important',
          '& [class*="_root_"], & [class*="_rootContentEditableWrapper_"], & .mdxeditor, & [contenteditable], & [class*="_contentEditable_"]':
            {
              backgroundColor: 'transparent !important',
              border: 'none !important',
              boxShadow: 'none !important',
            },
          '& [contenteditable]': { p: 0, minHeight: 'unset' },
          '&:focus-within': { borderColor: 'transparent', boxShadow: 'none' },
        }
      : {
          '& [class*="_toolbar_"]': { display: 'none' },
          '& [contenteditable]': { p: 0, minHeight: 'unset' },
          '&:focus-within': { borderColor: 'transparent', boxShadow: 'none' },
        };

    return (
      <RichTextEditor
        value={value}
        readOnly
        compact
        minHeight="unset"
        onBlur={onBlur}
        sx={transparentSx}
      />
    );
  }

  return (
    <Box sx={shadow ? { boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px' } : {}}>
      <RichTextEditor
        value={value}
        onChange={onChange}
        readOnly={false}
        compact
        minHeight={80}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder="Scrivi un commento..."
        sx={(theme: Theme) => ({
          bgcolor: 'background.paper',
          borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.divider,
          '& [class*="_toolbar_"]': { bgcolor: 'background.paper' },
          '& [contenteditable]': { py: 0.5 },
        })}
      />
    </Box>
  );
};

export default InputComment;
